import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Users, MapPin, Globe, Loader2, Search, CheckCircle2, Clock } from "lucide-react";

export default function Communities() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("All");
  const [memberships, setMemberships] = useState({});
  const [busyId, setBusyId] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/communities").then((r) => setList(r.data));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/my/communities").then((r) => {
      const map = {};
      (r.data || []).forEach((m) => { map[m.community_id] = m.status; });
      setMemberships(map);
    }).catch(() => {});
  }, [user]);

  const filtered = (list || []).filter((c) => {
    const qq = q.toLowerCase();
    const qOk = !q || c.name.toLowerCase().includes(qq) || c.city.toLowerCase().includes(qq) || c.curriculum.toLowerCase().includes(qq);
    const mOk = mode === "All" || c.mode === mode;
    return qOk && mOk;
  });

  const join = async (c) => {
    if (!user) {
      toast.info("Please sign in to join a community.");
      navigate("/login");
      return;
    }
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/communities/${c.id}/join`);
      setMemberships((m) => ({ ...m, [c.id]: data.status }));
      if (data.duplicate) toast.info(`You've already requested to join ${c.name}.`);
      else toast.success(`Request sent — ${c.name} admins will review shortly.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send join request");
    } finally {
      setBusyId(null);
    }
  };

  const buttonFor = (c) => {
    const status = memberships[c.id];
    if (status === "approved" || status === "member") return { label: "Community Member", icon: CheckCircle2, disabled: true, className: "chip" };
    if (status === "pending") return { label: "Request Pending", icon: Clock, disabled: true, className: "chip-orange" };
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-3xl mb-10">
        <div className="overline mb-3">Community Hub</div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">Find your tribe.</h1>
        <p className="mt-4 text-lg text-brand-muted">Local circles, online groups, and curriculum-specific communities across India.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input data-testid="community-search-input" className="field pl-11" placeholder="Search by city, curriculum, or name..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", "Offline", "Online", "Hybrid"].map((m) => (
            <button key={m} data-testid={`community-mode-${m.toLowerCase()}`} onClick={() => setMode(m)} className={`px-4 py-2 rounded-full text-sm font-medium border ${mode === m ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-brand-blue border-brand-border"}`}>{m}</button>
          ))}
        </div>
      </div>

      {!list ? <Loader2 className="animate-spin text-brand-green mx-auto" /> : (
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`community-card-${c.id}`} className="surface surface-hover p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-brand-blue">{c.name}</h3>
                  <div className="mt-1 flex items-center gap-3 text-sm text-brand-muted">
                    <span className="flex items-center gap-1"><MapPin size={13} /> {c.city}</span>
                    <span className="flex items-center gap-1"><Globe size={13} /> {c.language}</span>
                  </div>
                </div>
                <span className="chip">{c.mode}</span>
              </div>
              <p className="mt-3 text-sm text-brand-muted">{c.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                  <span className="chip-blue">{c.curriculum}</span>
                  <span className="chip-orange">Ages {c.age_group}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-brand-muted"><Users size={13} /> {c.members} members</div>
              </div>
              <button
                data-testid={`community-join-${c.id}`}
                onClick={() => join(c)}
                disabled={busyId === c.id || !!buttonFor(c)}
                className={`mt-5 w-full ${buttonFor(c) ? "chip flex items-center justify-center py-2.5" : "btn-outline"} disabled:cursor-not-allowed`}
              >
                {busyId === c.id ? (<><Loader2 className="animate-spin" size={14} /> Sending...</>) :
                  buttonFor(c) ? (<>{React.createElement(buttonFor(c).icon, { size: 14 })} {buttonFor(c).label}</>) :
                  "Request to join"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
